import {
  Box,
  Button,
  Field,
  Heading,
  Image,
  Input,
  Text,
  VStack
} from "@chakra-ui/react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { useResetPassword } from "../../hooks/useAuthQueries"
import { getAuthErrorMessage } from "../../utils/authErrors"

const ResetPassword: React.FC = () => {
  const [email, setEmail] = useState("")
  const [isSubmitted, setIsSubmitted] = useState(false)
  const resetPasswordMutation = useResetPassword()

  const getErrorMessage = (error: any) => getAuthErrorMessage(error, ["email"])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      await resetPasswordMutation.mutateAsync({ email })
      setIsSubmitted(true)
    } catch (error: any) {
      console.error("Password reset failed:", error)
    }
  }

  if (isSubmitted) {
    return (
      <>
        <Box pb={8}>
          <Link to="/">
            <Image
              w={{ base: 150, md: 200 }}
              src="/greenflip_logo.png"
              alt="GreenFlip"
            />
          </Link>
        </Box>

        <Box
          bg="gray.100"
          px={{ base: 6, md: 8, lg: 12 }}
          py={{ base: 6, md: 8, lg: 10 }}
          borderRadius="3xl"
          w={{ base: "95%", md: "85%", lg: "80%" }}
          maxW={{ base: "500px", lg: "500px" }}
        >
          <Heading pb={8} size={{ base: "lg", md: "xl" }}>
            Check your email
          </Heading>
          <Text pb={6} color="gray.700" textAlign="left">
            We've sent a password reset link to <strong>{email}</strong>. 
            Please check your email and click the link to reset your password.
          </Text>
        </Box>
        <Text textAlign="center" pt={8}>
          Didn't receive the email?{" "}
          <Text 
            display="inline" 
            color="green.800" 
            fontWeight="bold"
            cursor="pointer"
            onClick={() => setIsSubmitted(false)}
          >
            Try again
          </Text>
        </Text>
      </>
    )
  }

  return (
    <>
      <Box pb={8}>
        <Link to="/">
          <Image
            w={{ base: 150, md: 200 }}
            src="/greenflip_logo.png"
            alt="GreenFlip"
          />
        </Link>
      </Box>

      <Box
        bg="gray.100"
        px={{ base: 6, md: 8, lg: 12 }}
        py={{ base: 6, md: 8, lg: 10 }}
        borderRadius="3xl"
        w={{ base: "95%", md: "85%", lg: "80%" }}
        maxW={{ base: "500px", lg: "500px" }}
      >
        <Heading pb={8} size={{ base: "lg", md: "xl" }}>
          Forgot your password?
        </Heading>
        <Text pb={6} color="gray.700" textAlign="left">
          Enter your email address and we'll send you a link to reset your
          password.
        </Text>
        <form onSubmit={handleSubmit}>
          <VStack gap={5} align="stretch">
            {resetPasswordMutation.error && (
              <Box
                bg="red.50"
                border="1px solid"
                borderColor="red.200"
                color="red.800"
                px={4}
                py={3}
                borderRadius="md"
                fontSize="sm"
              >
                {getErrorMessage(resetPasswordMutation.error)}
              </Box>
            )}
            <Field.Root>
              <Field.Label fontSize="xs" fontWeight="bold">
                Email
              </Field.Label>
              <Input
                name="email"
                type="email"
                placeholder="Enter your email address"
                bg="white"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                borderColor={
                  (resetPasswordMutation.error as any)?.response?.data?.email ? "red.300" : undefined
                }
                _focus={{
                  borderColor: (resetPasswordMutation.error as any)?.response?.data?.email ? "red.500" : "green.500"
                }}
              />
              {(resetPasswordMutation.error as any)?.response?.data?.email && (
                <Text color="red.500" fontSize="xs" mt={1}>
                  {(resetPasswordMutation.error as any).response.data.email[0]}
                </Text>
              )}
            </Field.Root>

            <Button
              type="submit"
              bg="green.800"
              color="white"
              _hover={{ bg: "green.900" }}
              size="lg"
              disabled={resetPasswordMutation.isPending}
            >
              {resetPasswordMutation.isPending ? "Sending..." : "Send reset link"}
            </Button>
          </VStack>
        </form>
      </Box>
      <Text textAlign="center" pt={8}>
        Remember your password?{" "}
        <Text display="inline" color="green.800" fontWeight="bold">
          <Link to="/login">Sign in</Link>
        </Text>
      </Text>
    </>
  )
}

export default ResetPassword
